import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { fmtNumber } from '@/lib/format';

export function WatchlistCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['watchlist'], queryFn: api.watchlist, refetchInterval: 30_000 });
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState('');

  const add = useMutation({
    mutationFn: (ticker: string) => api.addWatch(ticker),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['watchlist'] });
      setInput('');
      setAdding(false);
      toast.success('Added to watchlist');
    },
    onError: () => toast.error('Add failed'),
  });
  const remove = useMutation({
    mutationFn: (ticker: string) => api.removeWatch(ticker),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist'] }),
  });

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Eye className="size-4 text-indigo-300" />
          <h3 className="text-sm font-medium text-zinc-200">Watchlist</h3>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="p-1 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900"
          title="Add ticker"
        >
          <Plus className="size-4" />
        </button>
      </div>

      {adding && (
        <form
          className="flex items-center gap-1.5 mb-2"
          onSubmit={(e) => {
            e.preventDefault();
            const t = input.trim().toUpperCase();
            if (!t) return;
            // Accept "AAPL" by auto-suffixing to T212 format.
            const ticker = t.includes('_') ? t : `${t}_US_EQ`;
            add.mutate(ticker);
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="AAPL or AAPL_US_EQ"
            autoFocus
            className="flex-1 bg-zinc-900/60 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100 outline-none focus:border-indigo-500/60"
          />
          <button
            type="submit"
            disabled={add.isPending}
            className="text-[11px] px-2 py-1 rounded bg-indigo-500/20 border border-indigo-500/40 text-indigo-100"
          >
            Add
          </button>
        </form>
      )}

      {!q.data || q.data.items.length === 0 ? (
        <div className="text-xs text-zinc-400 py-4 text-center">
          {adding ? 'Add a ticker above…' : 'No watched tickers.'}
        </div>
      ) : (
        <ul className="space-y-1">
          {q.data.items.map((w) => (
            <li key={w.ticker} className="flex items-center gap-2 py-1">
              <Link
                to="/positions/$ticker"
                params={{ ticker: w.ticker }}
                className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-90"
              >
                <span className="font-mono text-xs text-zinc-300 w-12 shrink-0">
                  {w.ticker.split('_')[0]}
                </span>
                <span className="text-xs text-zinc-400 truncate flex-1 min-w-0">
                  {w.name}
                </span>
                <span className="text-xs num text-zinc-100 w-16 text-right">
                  {fmtNumber(w.currentPrice)}
                </span>
              </Link>
              <button
                type="button"
                onClick={() => remove.mutate(w.ticker)}
                className="p-2 text-zinc-400 hover:text-rose-400 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                title="Remove"
                aria-label={`Remove ${w.ticker.split('_')[0]} from watchlist`}
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
